import {
  PDF_CONTENT_WIDTH_PT,
  SCALE_MAX,
  SCALE_MIN,
  clampPlacement,
  computePreviewLayout,
  defaultPlacement,
  resolveLogoPlacement,
  type LogoPlacement,
} from "../domain/pdfHeader";
import { buildClassicPreviewMetaLines, getHeaderTemplateSpec, resolvePdfTemplateId, type MetaLineSpec } from "../domain/pdfHeader/templates";
import { showToast } from "./ui";

export interface LogoEditorContext {
  /** Business profile used for preview meta (invoice title, number, dates). */
  business: Record<string, unknown>;
}

export interface LogoEditorState {
  sourceB64: string;
  placement: LogoPlacement;
  enabled: boolean;
  templateId: string;
}

const UPLOAD_ICON_SVG = `<svg class="logo-upload-icon" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
  <path d="M12 4v10M8 10l4-4 4 4M5 20h14" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

function logoPlacementSlotHtml(state: LogoEditorState): string {
  if (!state.sourceB64) {
    return `
      <button type="button" class="logo-upload-target" id="logo-upload-target" aria-label="Upload logo image">
        ${UPLOAD_ICON_SVG}
        <span class="logo-upload-label">Add logo</span>
        <span class="logo-upload-hint">PNG or JPEG</span>
      </button>`;
  }
  return `
    <div class="logo-selection-frame" id="logo-selection-frame">
      <img id="logo-drag-img" src="${state.sourceB64}" alt="" draggable="false">
      <button type="button" class="logo-selection-handle" id="logo-resize-handle" aria-label="Resize logo"></button>
    </div>`;
}

export function logoEditorHtml(state: LogoEditorState): string {
  const templateLabel =
    state.templateId === "classic" ? "Classic template" : state.templateId;
  const emptyClass = state.sourceB64 ? "" : " logo-placement-slot--empty";
  return `
    <div class="logo-editor" id="logo-editor">
      <input type="file" id="logo-file" accept="image/png,image/jpeg" hidden>
      <label class="checkbox"><input type="checkbox" id="logo-enabled" ${state.enabled ? "checked" : ""} ${state.sourceB64 ? "" : "disabled"}> Show logo on invoices</label>
      <p class="hint logo-template-hint">${templateLabel}</p>
      <div class="logo-preview-outer" id="logo-preview-outer">
        <div class="logo-preview-scale" id="logo-preview-scale">
          <div class="logo-header-canvas" id="logo-header-canvas" aria-label="Invoice header preview at PDF scale">
            <div class="logo-header-band" id="logo-header-band">
              <div class="logo-header-logo-col" id="logo-header-logo-col">
                <div class="logo-header-placement-slot${emptyClass}" id="logo-header-placement-slot">
                  ${logoPlacementSlotHtml(state)}
                </div>
              </div>
              <div class="logo-header-meta-col" id="logo-header-meta-col">
                <div class="logo-meta-placement-slot" id="logo-meta-placement-slot"></div>
              </div>
            </div>
            <div class="logo-header-break" aria-hidden="true"></div>
          </div>
        </div>
      </div>
      ${
        state.sourceB64
          ? `<div class="logo-editor-actions">
              <button type="button" class="btn small ghost" id="replace-logo">Replace</button>
              <button type="button" class="btn small ghost" id="clear-logo">Remove</button>
            </div>`
          : ""
      }
      <p class="hint logo-editor-hint">Drag to position; use the corner handle to resize. Preview matches the PDF. What you see is what prints.</p>
    </div>`;
}

/** Sync full business record including logo source (required for server-side PDF). */
export function businessRecordForSync(record: Record<string, unknown>): Record<string, unknown> {
  return { ...record };
}

function prefersPngSource(file: File): boolean {
  const type = String(file.type || "").toLowerCase();
  if (type === "image/png" || type.includes("png")) return true;
  return /\.png$/i.test(file.name || "");
}

function isContentPixel(r: number, g: number, b: number, a: number): boolean {
  if (a <= 12) return false;
  if (a > 240 && r >= 248 && g >= 248 && b >= 248) return false;
  return true;
}

/** Crop upload to visible pixels so natural dimensions match what we layout. */
function cropCanvasToContent(source: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = source.getContext("2d");
  if (!ctx) return source;
  const { width, height } = source;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (isContentPixel(data[i], data[i + 1], data[i + 2], data[i + 3])) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return source;
  minX = Math.max(0, minX - 1);
  minY = Math.max(0, minY - 1);
  maxX = Math.min(width - 1, maxX + 1);
  maxY = Math.min(height - 1, maxY);
  const cw = maxX - minX + 1;
  const ch = maxY - minY + 1;
  const out = document.createElement("canvas");
  out.width = cw;
  out.height = ch;
  const octx = out.getContext("2d");
  if (!octx) return source;
  octx.clearRect(0, 0, cw, ch);
  octx.drawImage(source, minX, minY, cw, ch, 0, 0, cw, ch);
  return out;
}

function canvasToPngDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL("image/png");
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load logo image."));
    img.src = dataUrl;
  });
}

export async function normalizeLogoSourceDataUrl(dataUrl: string): Promise<string> {
  const img = await loadImage(dataUrl);
  const raw = document.createElement("canvas");
  raw.width = Math.max(1, img.naturalWidth);
  raw.height = Math.max(1, img.naturalHeight);
  const ctx = raw.getContext("2d");
  if (!ctx) throw new Error("Could not process logo.");
  ctx.clearRect(0, 0, raw.width, raw.height);
  ctx.drawImage(img, 0, 0);
  return canvasToPngDataUrl(cropCanvasToContent(raw));
}

/** Downscale upload; keep PNG alpha; tight-crop for consistent layout dimensions. */
export async function fileToSourceDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const maxEdge = 1200;
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process logo.");
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  if (!prefersPngSource(file)) {
    const jpegCanvas = document.createElement("canvas");
    jpegCanvas.width = w;
    jpegCanvas.height = h;
    const jctx = jpegCanvas.getContext("2d");
    if (!jctx) throw new Error("Could not process logo.");
    jctx.fillStyle = "#ffffff";
    jctx.fillRect(0, 0, w, h);
    jctx.drawImage(canvas, 0, 0);
    return canvasToPngDataUrl(cropCanvasToContent(jpegCanvas));
  }
  return canvasToPngDataUrl(cropCanvasToContent(canvas));
}

function renderMetaColumn(slot: HTMLElement, lines: MetaLineSpec[]) {
  slot.innerHTML = lines
    .map(
      (line) =>
        `<div class="logo-meta-line${line.bold ? " logo-meta-line--title" : ""}" style="font-size:${line.size}pt">${line.text}</div>`
    )
    .join("");
}

function previewScaleFactor(outerWidthPx: number): number {
  const ptToPx = 96 / 72;
  const canvasPxW = PDF_CONTENT_WIDTH_PT * ptToPx;
  if (!outerWidthPx || !canvasPxW) return 1;
  return outerWidthPx / canvasPxW;
}

function clientDeltaToPt(deltaPx: number, root: ParentNode): number {
  const outer = root.querySelector("#logo-preview-outer") as HTMLElement | null;
  const outerW = outer?.clientWidth || 1;
  const factor = previewScaleFactor(outerW);
  const ptToPx = 96 / 72;
  return deltaPx / factor / ptToPx;
}

function innerLogoColumnPt(templateId: string): number {
  const spec = getHeaderTemplateSpec(templateId as "classic");
  return PDF_CONTENT_WIDTH_PT * spec.slotFraction - spec.logoInsetLeftPt - spec.logoInsetRightPt;
}

function scaleFromWidthPt(widthPt: number, templateId: string): number {
  const inner = innerLogoColumnPt(templateId);
  if (!inner) return 1;
  return clampPlacement({ scale: widthPt / inner }).scale;
}

/**
 * Position logo and size the preview using the same layout function as classic.js PDF.
 * Scales up/down to fill the container width on any screen.
 */
function applyPreviewLayout(
  root: ParentNode,
  state: LogoEditorState,
  ctx: LogoEditorContext,
  img: HTMLImageElement | null
) {
  const outer = root.querySelector("#logo-preview-outer") as HTMLElement | null;
  const scaleWrap = root.querySelector("#logo-preview-scale") as HTMLElement | null;
  const canvas = root.querySelector("#logo-header-canvas") as HTMLElement | null;
  const band = root.querySelector("#logo-header-band") as HTMLElement | null;
  const logoCol = root.querySelector("#logo-header-logo-col") as HTMLElement | null;
  const placementSlot = root.querySelector("#logo-header-placement-slot") as HTMLElement | null;
  const selectionFrame = root.querySelector("#logo-selection-frame") as HTMLElement | null;
  const metaCol = root.querySelector("#logo-header-meta-col") as HTMLElement | null;
  const metaSlot = root.querySelector("#logo-meta-placement-slot") as HTMLElement | null;
  if (!outer || !scaleWrap || !canvas || !band || !logoCol || !placementSlot || !metaCol || !metaSlot) return;

  const metaLines = buildClassicPreviewMetaLines(ctx.business);
  renderMetaColumn(metaSlot, metaLines);

  const naturalW = img?.naturalWidth || 0;
  const naturalH = img?.naturalHeight || 0;
  const { layout } = computePreviewLayout(
    { ...ctx.business, logo_enabled: state.enabled },
    metaLines,
    naturalW,
    naturalH,
    state.placement
  );

  canvas.style.width = `${PDF_CONTENT_WIDTH_PT}pt`;
  band.style.height = `${layout.headerBandHeightPt}pt`;
  logoCol.style.width = `${layout.leftColumnWidthPt}pt`;
  metaCol.style.width = `${PDF_CONTENT_WIDTH_PT - layout.leftColumnWidthPt}pt`;

  placementSlot.style.left = `${layout.logoInsetLeftPt}pt`;
  placementSlot.style.right = `${layout.logoInsetRightPt}pt`;
  placementSlot.style.top = `${layout.logoInsetTopPt}pt`;
  placementSlot.style.bottom = `${layout.logoInsetBottomPt}pt`;

  const showLogo = Boolean(img && naturalW > 0 && state.enabled && state.sourceB64);
  if (selectionFrame && showLogo) {
    selectionFrame.style.display = "block";
    selectionFrame.style.width = `${layout.logoWidthPt}pt`;
    selectionFrame.style.height = `${layout.logoHeightPt}pt`;
    selectionFrame.style.left = `${layout.logoXInColumn - layout.logoInsetLeftPt}pt`;
    selectionFrame.style.bottom = "0";
    if (img) {
      img.style.display = "block";
      img.style.width = "100%";
      img.style.height = "100%";
    }
  } else if (selectionFrame) {
    selectionFrame.style.display = "none";
    if (img) img.style.display = "none";
  }

  metaSlot.style.left = `${layout.metaInsetLeftPt}pt`;
  metaSlot.style.right = `${layout.metaInsetRightPt}pt`;
  metaSlot.style.top = `${layout.metaInsetTopPt}pt`;
  metaSlot.style.bottom = `${layout.metaInsetBottomPt}pt`;

  const outerW = outer.clientWidth || outer.getBoundingClientRect().width;
  if (outerW > 0) {
    const ptToPx = 96 / 72;
    const factor = previewScaleFactor(outerW);
    scaleWrap.style.width = `${PDF_CONTENT_WIDTH_PT}pt`;
    scaleWrap.style.transform = `scale(${factor})`;
    scaleWrap.style.transformOrigin = "top left";
    const bandPx = layout.headerBandHeightPt * ptToPx * factor;
    const dividerPx = layout.dividerHeightPt * ptToPx * factor;
    outer.style.height = `${bandPx + dividerPx}px`;
  }
}

async function ingestLogoFile(
  file: File,
  state: LogoEditorState,
  onChange: (next: LogoEditorState, reason: "source" | "placement" | "enabled") => void
) {
  if (file.size > 8 * 1024 * 1024) throw new Error("Logo must be under 8 MB.");
  state.sourceB64 = await fileToSourceDataUrl(file);
  state.placement = defaultPlacement();
  state.enabled = true;
  onChange({ ...state }, "source");
}

export function refreshLogoEditorSection(
  root: ParentNode,
  state: LogoEditorState,
  ctx: LogoEditorContext,
  onChange: (next: LogoEditorState, reason: "source" | "placement" | "enabled") => void
) {
  const editor = root.querySelector("#logo-editor");
  if (!editor) return;
  const replacement = document.createElement("div");
  replacement.innerHTML = logoEditorHtml(state).trim();
  const nextEditor = replacement.firstElementChild;
  if (!nextEditor) return;
  editor.replaceWith(nextEditor);
  wireLogoEditor(root, state, ctx, onChange);
}

export function wireLogoEditor(
  root: ParentNode,
  state: LogoEditorState,
  ctx: LogoEditorContext,
  onChange: (next: LogoEditorState, reason: "source" | "placement" | "enabled") => void
) {
  const placementSlot = root.querySelector("#logo-header-placement-slot") as HTMLElement | null;
  const selectionFrame = root.querySelector("#logo-selection-frame") as HTMLElement | null;
  const img = root.querySelector("#logo-drag-img") as HTMLImageElement | null;
  const resizeHandle = root.querySelector("#logo-resize-handle") as HTMLButtonElement | null;
  const fileInput = root.querySelector("#logo-file") as HTMLInputElement | null;
  const enabled = root.querySelector("#logo-enabled") as HTMLInputElement | null;

  const repaint = () => applyPreviewLayout(root, state, ctx, img);

  img?.addEventListener("load", repaint);
  window.addEventListener("resize", repaint);
  requestAnimationFrame(repaint);

  if (state.sourceB64 && img) {
    void normalizeLogoSourceDataUrl(state.sourceB64)
      .then((normalized) => {
        if (normalized !== state.sourceB64) {
          state.sourceB64 = normalized;
          img.src = normalized;
          onChange({ ...state }, "source");
          repaint();
        }
      })
      .catch(() => {
        /* keep existing */
      });
  }

  const openFilePicker = () => fileInput?.click();

  const onFileChosen = async (file: File | undefined) => {
    if (!file) return;
    try {
      await ingestLogoFile(file, state, onChange);
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Logo failed.", "error");
    } finally {
      if (fileInput) fileInput.value = "";
    }
  };

  fileInput?.addEventListener("change", async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    await onFileChosen(file);
  });

  root.querySelector("#logo-upload-target")?.addEventListener("click", openFilePicker);
  root.querySelector("#replace-logo")?.addEventListener("click", openFilePicker);

  enabled?.addEventListener("change", () => {
    state.enabled = Boolean(enabled.checked);
    repaint();
    onChange({ ...state }, "enabled");
  });

  root.querySelector("#clear-logo")?.addEventListener("click", () => {
    state.sourceB64 = "";
    state.placement = defaultPlacement();
    state.enabled = false;
    onChange({ ...state }, "source");
  });

  if (placementSlot) {
    placementSlot.addEventListener("dragover", (ev) => {
      ev.preventDefault();
      placementSlot.classList.add("logo-placement-slot--dragover");
    });
    placementSlot.addEventListener("dragleave", () => {
      placementSlot.classList.remove("logo-placement-slot--dragover");
    });
    placementSlot.addEventListener("drop", async (ev) => {
      ev.preventDefault();
      placementSlot.classList.remove("logo-placement-slot--dragover");
      const file = ev.dataTransfer?.files?.[0];
      if (!file || !file.type.startsWith("image/")) {
        showToast("Drop a PNG or JPEG image.", "error");
        return;
      }
      await onFileChosen(file);
    });
  }

  if (selectionFrame) {
    let moving = false;
    let startX = 0;
    let startOx = 0;

    selectionFrame.addEventListener("pointerdown", (ev) => {
      if ((ev.target as Element).closest("#logo-resize-handle")) return;
      if (!state.enabled) return;
      moving = true;
      selectionFrame.setPointerCapture(ev.pointerId);
      startX = ev.clientX;
      startOx = state.placement.offset_x;
      selectionFrame.classList.add("logo-selection-frame--dragging");
      ev.preventDefault();
    });
    selectionFrame.addEventListener("pointermove", (ev) => {
      if (!moving) return;
      const innerColumnPt = innerLogoColumnPt(state.templateId);
      const dxPt = clientDeltaToPt(ev.clientX - startX, root);
      state.placement = clampPlacement({
        ...state.placement,
        offset_x: startOx + dxPt / innerColumnPt,
      });
      repaint();
    });
    const endMove = () => {
      if (!moving) return;
      moving = false;
      selectionFrame.classList.remove("logo-selection-frame--dragging");
      onChange({ ...state }, "placement");
    };
    selectionFrame.addEventListener("pointerup", endMove);
    selectionFrame.addEventListener("pointercancel", endMove);
  }

  if (resizeHandle && img) {
    let resizing = false;
    let startX = 0;
    let startWidthPt = 0;

    resizeHandle.addEventListener("pointerdown", (ev) => {
      if (!state.enabled) return;
      resizing = true;
      resizeHandle.setPointerCapture(ev.pointerId);
      startX = ev.clientX;
      const naturalW = img.naturalWidth || 0;
      const naturalH = img.naturalHeight || 0;
      const { layout } = computePreviewLayout(
        { ...ctx.business, logo_enabled: state.enabled },
        buildClassicPreviewMetaLines(ctx.business),
        naturalW,
        naturalH,
        state.placement
      );
      startWidthPt = layout.logoWidthPt;
      selectionFrame?.classList.add("logo-selection-frame--resizing");
      ev.preventDefault();
      ev.stopPropagation();
    });
    resizeHandle.addEventListener("pointermove", (ev) => {
      if (!resizing) return;
      const innerColumnPt = innerLogoColumnPt(state.templateId);
      const dxPt = clientDeltaToPt(ev.clientX - startX, root);
      const minW = innerColumnPt * SCALE_MIN;
      const maxW = innerColumnPt * SCALE_MAX;
      const nextWidthPt = Math.max(minW, Math.min(maxW, startWidthPt + dxPt));
      state.placement = clampPlacement({
        ...state.placement,
        scale: scaleFromWidthPt(nextWidthPt, state.templateId),
      });
      repaint();
    });
    const endResize = () => {
      if (!resizing) return;
      resizing = false;
      selectionFrame?.classList.remove("logo-selection-frame--resizing");
      onChange({ ...state }, "placement");
    };
    resizeHandle.addEventListener("pointerup", endResize);
    resizeHandle.addEventListener("pointercancel", endResize);
  }
}

export function readLogoStateFromRecord(record: Record<string, unknown>): LogoEditorState {
  const templateId = resolvePdfTemplateId(record);
  const placement = resolveLogoPlacement(record, templateId);
  const sourceB64 = String(record.logo_source_b64 || record.logo_b64 || "");
  return {
    sourceB64,
    placement,
    enabled: record.logo_enabled !== false && Boolean(sourceB64),
    templateId,
  };
}

export function logoProfilePatch(
  state: LogoEditorState,
  existing: Record<string, unknown>
): Record<string, unknown> {
  const clamped = clampPlacement(state.placement);
  const prior =
    existing.logo_placement_by_template && typeof existing.logo_placement_by_template === "object"
      ? (existing.logo_placement_by_template as Record<string, LogoPlacement>)
      : {};
  return {
    ...existing,
    logo_placement_by_template: {
      ...prior,
      [state.templateId]: clamped,
    },
    logo_placement: clamped,
    logo_source_b64: state.sourceB64 || "",
    logo_enabled: state.enabled && Boolean(state.sourceB64),
    pdf_template: state.templateId,
  };
}
